<!--
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="code-viewer-wrapper">
    <div v-if="isLoading" class="loading-container">
      <PrimeProgressSpinner />
    </div>
    <div v-else-if="error" class="error-container">
      <i class="ri-error-warning-line mr-2 text-2xl" aria-hidden="true"></i>
      <p>{{ error }}</p>
    </div>
    <div v-else>
      <!-- Markdown/HTML Preview -->
      <iframe
        v-if="isMarkdown || isHtml"
        :srcdoc="iframeContent"
        class="preview-iframe w-full h-screen"
        sandbox="allow-scripts allow-popups"
      ></iframe>
      <!-- Code Editor (read-only) -->
      <div v-else ref="editorContainer" class="h-full w-full"></div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import PrimeProgressSpinner from "primevue/progressspinner";
import {
  getLanguageExtension,
  getBaseExtensions,
  EditorState,
  EditorView,
} from "@/composables/useCodeMirror.js";
import { useMarkdownPreview } from "@/composables/useMarkdownPreview.js";
import { useTheme } from "@/composables/useTheme.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";

const { t } = useI18n();
const { isDarkMode } = useTheme();

const props = defineProps({
  contentNode: { type: Object, required: true },
  isSharedLink: { type: Boolean, default: false },
});

const editorContent = ref("");
const currentMimeType = ref(null);
const editorContainer = ref(null);
const editorView = shallowRef(null);
const isLoading = ref(false);
const error = ref(null);

// --- Markdown Preview ---
const { isMarkdown, isHtml, iframeContent } = useMarkdownPreview({
  content: editorContent,
  mimeType: currentMimeType,
});

const createEditor = () => {
  if (!editorContainer.value) {
    return;
  }

  const langExtension = getLanguageExtension(currentMimeType.value);
  const baseExtensions = getBaseExtensions({
    editable: false,
    showLineNumbers: true,
    enableHistory: false,
    dark: isDarkMode(),
  });

  const extensions = [...baseExtensions];
  if (
    langExtension &&
    (Array.isArray(langExtension) ? langExtension.length > 0 : true)
  ) {
    extensions.push(langExtension);
  }

  const state = EditorState.create({ doc: editorContent.value, extensions });
  editorView.value = new EditorView({ state, parent: editorContainer.value });
};

const destroyEditor = () => {
  if (editorView.value) {
    editorView.value.destroy();
    editorView.value = null;
  }
};

const loadContent = async (node) => {
  if (!node.id && !node.shareId) {
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    let blob;
    if (props.isSharedLink && node.shareId) {
      blob = await alfrescoNodeService.getSharedLinkContent(node.shareId);
    } else {
      blob = await alfrescoNodeService.getNodeContent(node.id, false);
    }

    let text = await blob.text();
    if (node.content?.mimeType === "application/json") {
      try {
        text = JSON.stringify(JSON.parse(text), null, 2);
      } catch (e) {
        // JSON is malformed, display as-is
        console.warn(
          "Failed to format JSON, displaying raw content:",
          e.message,
        );
      }
    }

    editorContent.value = text;
    currentMimeType.value = node.content?.mimeType || "text/plain";

    isLoading.value = false;
    await nextTick();

    if (!isMarkdown.value && !isHtml.value) {
      createEditor();
    }
  } catch (err) {
    console.error("Failed to load content:", err);
    error.value = t("editor.loadError");
    isLoading.value = false;
  }
};

watch(
  () => props.contentNode,
  (newNode) => {
    if (newNode) {
      destroyEditor();
      loadContent(newNode);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  destroyEditor();
});
</script>

<style scoped>
.code-viewer-wrapper {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.loading-container,
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  min-height: 200px;
  text-align: center;
}

.error-container {
  color: var(--p-orange-500);
}

.preview-iframe {
  border: none;
  height: 100%;
}

.h-screen {
  height: 100vh;
}
</style>
